import React, { FC, ReactNode } from 'react';
import {
  IComponentCommonProps,
  IHeaderGroupComponentProps,
  TSDMCardScrollType,
  TSDMCardUpType,
  TSDMFeatureCardsList,
} from '@ldp/common-types';
import { HeaderGroupComponent } from '../header-group/header-group.component';
import { FeatureCards } from '@ldp-web/component-library/ui/organisms/feature-cards';
import { useSimpleTranslation } from '@ldp/simple-i18n';
import { useRedirectLink } from '../../utils';
import { useSDMRouterContext } from '@ldp/sdm-contexts';
import { WrapperComponent } from '../wrapper';
import { useAnalyticsContext, useFeatureFlags } from '@ldp/common-contexts';
import {
  SDMAnalyticEvents,
  TCTAContextAnalytics,
  TSDMComponentAnalyticsObject,
  TVerticalFeatureCardContainerCTAContext,
  TrackingFnContext,
  getCommonComponentContext,
} from '@ldp/sdm-analytics-utils';

/** Don't forget to add prop types */
export interface IVerticalFeatureCardComponentProps extends IComponentCommonProps {
  children?: ReactNode;
  /** up number decides number of cards in a row */
  upNumber: TSDMCardUpType;
  componentId?: string;
  /** scroll type decides whether to show scroller or in grid */
  scrollOverflow: TSDMCardScrollType;
  /** feature cards list */
  verticalFeatureCards: TSDMFeatureCardsList[];
  /** header group */
  headerGroup?: IHeaderGroupComponentProps;
  /** content id of component */
  contentId?: string;
  /** analytics object */
  analytics?: TSDMComponentAnalyticsObject;
  /** id of component */
  id?: string;
}

/** Renders Vertical Feature Cards in a scroller or grid.
 * Mock Data will be replaced with props once actual data from BFF is ready */
export const VerticalFeatureCardComponent: FC<IVerticalFeatureCardComponentProps> = (props) => {
  const {
    upNumber,
    scrollOverflow,
    verticalFeatureCards,
    headerGroup,
    styling,
    componentId,
    contentId,
    analytics,
    id,
  } = props;
  const i18n = useSimpleTranslation();
  const router = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const scrollOverflowValue = scrollOverflow.toLowerCase() as TSDMCardScrollType;
  /** adds a new key called 'callToActionModal' to the object */
  const updatedCardsList = verticalFeatureCards
    .map((i) => {
      const featureCard = i as TSDMFeatureCardsList;
      if (featureCard?.callToActionLink && 'modalTitle' in featureCard.callToActionLink) {
        const { callToActionLink, ...rest } = featureCard;
        return {
          ...rest,
          callToActionModal: callToActionLink,
        };
      } else {
        return { ...i };
      }
    })
    .filter((card) => card) as TSDMFeatureCardsList[];

  const { trackEvent } = useAnalyticsContext();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow: snowplowEnabled } = getFeatureFlags();

  const trackFnWrapper: TrackingFnContext = (type, context) => {
    const { actionType, callToActionElement, position } = context as TCTAContextAnalytics;
    const dsModelData = analytics?.dsModel;
    const engineValue = dsModelData?.engine || '';
    const commonContext = getCommonComponentContext(snowplowEnabled, true, dsModelData);
    const headlineText = headerGroup?.headline?.text || '';
    const featureCardIndex = position ? position - 1 : 0;
    const selectedFeatureCard = updatedCardsList[featureCardIndex];
    const { contentId: verticalFeatureCardContentId, headline: verticalFeatureCardHeadline } = selectedFeatureCard;

    const eventTrackingContext: TVerticalFeatureCardContainerCTAContext = {
      event: type as SDMAnalyticEvents,
      parentComponent: {
        id: contentId || '',
        type: componentId || '',
        name: headlineText,
        engine: engineValue,
      },
      component: {
        id: verticalFeatureCardContentId || '',
        type: 'verticalFeatureCards',
        name: verticalFeatureCardHeadline,
        click_element: 'callToAction',
        click_action: callToActionElement?.text,
        click_url: callToActionElement?.url,
        engine: engineValue,
        position: position,
        number_of_children: verticalFeatureCards.length,
        parent_id: contentId,
        hierarchy: [headlineText, verticalFeatureCardHeadline],
      },
      customEngagement: {
        action: actionType,
      },
      ...commonContext,
    };

    trackEvent('heliosVerticalFeatureCardContainer', eventTrackingContext);
  };

  /** set no padding for PLP only */
  const IS_SSR = typeof window === 'undefined';

  const isNoPadding = !IS_SSR
    ? window.location.pathname.includes('/c/') || window.location.pathname.includes('/plp')
    : false;

  return (
    <WrapperComponent id={id} data-testid="vertical-feature-cards" styling={styling} isNoPadding={isNoPadding}>
      {headerGroup && <HeaderGroupComponent {...headerGroup} noWrapper />}
      <FeatureCards
        upNumber={upNumber}
        scrollType={scrollOverflowValue}
        cardsList={updatedCardsList as any}
        i18nskip={i18n('skip')}
        skipLinkLabel={i18n('a11ySection')}
        variant="vertical"
        cardClickAction={redirectToPage}
        trackingFn={trackFnWrapper}
      />
    </WrapperComponent>
  );
};

export default VerticalFeatureCardComponent;
